import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, NEUTRAL, OPEN_SHIFTS } from "../../../../theme/tokens";
import type { BaseRequest } from "../../../../utils/models";
import { GAPS_DAY_LABEL_FORMAT } from "../gapsLabels";
import { type GapsRequestDayGroup, GapsRequestDayKind } from "../gapsRequests";

type Props = {
	group: Pick<GapsRequestDayGroup<BaseRequest>, "kind" | "dayKey" | "day">;
	/** How many of the day's requests are urgent claims — kept on the header as `data-band` for what reads the list. */
	urgentCount: number;
	/** How many shift cards the day lists. */
	shiftCount: number;
};

/**
 * A day on Open Shifts, as the design's Option A heads it (APP-886): its name and how many shifts it lists, on the
 * list's own tint, sticky while its cards scroll under it. Urgency is each card's own "Starts in" chip, not the day's.
 *
 * "Today" is the CALENDAR day (P2-15), never a staffing day relabelled, and a day that is not today or tomorrow is only
 * ever named by its date.
 */
export const GapsRequestDayHeader: FC<Props> = ({ group, urgentCount, shiftCount }) => {
	const { t } = useTranslation();
	// The artifact's day label: "Today · Thu 24 Sep", "Tomorrow · Fri 25 Sep", else the date alone.
	const dateLabel = group.day.toFormat(GAPS_DAY_LABEL_FORMAT);
	const dayName =
		group.kind === GapsRequestDayKind.Today ? t("gaps.requests.today") : group.kind === GapsRequestDayKind.Tomorrow ? t("gaps.requests.tomorrow") : null;

	return (
		<Box
			component="h2"
			data-testid={`gaps-requests-day-${group.dayKey}`}
			data-band={urgentCount > 0}
			sx={{
				position: "sticky",
				top: 0,
				zIndex: 1,
				m: 0,
				display: "flex",
				alignItems: "baseline",
				gap: 0.75,
				px: OPEN_SHIFTS.LIST_INSET + 0.5,
				pt: 2,
				pb: 1,
				bgcolor: NEUTRAL[50],
				fontSize: FONT_SIZE.TITLE_SM,
				fontWeight: 400,
				color: INK.SECONDARY,
			}}
		>
			<Box component="span" sx={{ fontWeight: 700, color: INK.PRIMARY }}>
				{dayName ?? dateLabel}
			</Box>
			{dayName !== null && <Box component="span">{`· ${dateLabel}`}</Box>}
			<Typography
				component="span"
				data-testid={`gaps-requests-day-${group.dayKey}-count`}
				sx={{ ml: "auto", fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
			>
				{t("gaps.requests.dayShifts", { count: shiftCount })}
			</Typography>
		</Box>
	);
};
